import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { db } from './config'
import { getBlockedStoreSellerIds } from './shopify'

/**
 * A buyer's block list.
 *
 * It lives on the buyer's own user doc under `blockedSellers`, alongside
 * `cartList` and `recipients`: one entry per blocked account, carrying the
 * name and avatar so the management list renders without a read per seller.
 *
 * A seller is blocked as an ACCOUNT, which is what makes "including all his
 * stores" work. A local listing's `seller` is that account's uid, while a
 * Shopify listing's `seller_id` is the role id of the store it came from --
 * so hiding everything a blocked account sells means matching on the uid
 * plus one role id per store they own, resolved through the backend.
 */
const readBlockedSellers = (userData) => {
    const blocked = userData?.blockedSellers
    return Array.isArray(blocked) ? blocked.filter((entry) => entry?.id) : []
}

export const getBlockedSellers = async (buyerUid) => {
    if (!buyerUid) return []

    try {
        const snapshot = await getDoc(doc(db, 'users', buyerUid))
        return readBlockedSellers(snapshot.data())
    } catch (error) {
        console.error('Error getting blocked sellers:', error)
        return []
    }
}

const resolveBlockedSellerIds = async (buyerUid) => {
    const blockedUids = (await getBlockedSellers(buyerUid)).map((entry) => entry.id)

    if (blockedUids.length === 0) {
        return { ids: [], complete: true }
    }

    try {
        return { ids: [...blockedUids, ...(await getBlockedStoreSellerIds())], complete: true }
    } catch (error) {
        // The blocked account's own listings are still hidden; the Shopify
        // stores it owns are not, until an attempt succeeds. Reported so a
        // partial block list is visible in the console rather than silent,
        // and not cached, so the next page load retries.
        console.error("Error resolving blocked sellers' Shopify stores:", error)
        return { ids: blockedUids, complete: false }
    }
}

/**
 * Every seller id to hide from a buyer: the blocked accounts' uids plus the
 * store role ids they sell under.
 *
 * Resolving this costs a Firestore read and a backend call, and the browse
 * grid needs it on every filter change, so a fully resolved list is cached
 * for the page's lifetime like the store lookups in ./shopify. Blocking and
 * unblocking both drop the entry, so a filter run after either reflects the
 * change immediately.
 */
const blockedIdCache = new Map()

export const clearBlockedSellerCache = (buyerUid) => {
    if (buyerUid) {
        blockedIdCache.delete(buyerUid)
    } else {
        blockedIdCache.clear()
    }
}

export const getBlockedSellerIds = async (buyerUid) => {
    // Signed out: nothing is blocked, and nothing is read.
    if (!buyerUid) return new Set()

    if (blockedIdCache.has(buyerUid)) {
        return new Set(blockedIdCache.get(buyerUid))
    }

    const { ids, complete } = await resolveBlockedSellerIds(buyerUid)
    if (complete) {
        blockedIdCache.set(buyerUid, ids)
    }

    return new Set(ids)
}

export const isBlockedItem = (item, blockedSellerIds) => {
    if (!item || !blockedSellerIds || blockedSellerIds.size === 0) return false
    // `seller` for a local listing, `seller_id` for a Shopify one; both are
    // checked because normalizeShopifyProduct sets each to the store role id
    // and either may be the one a caller carries.
    return blockedSellerIds.has(item.seller) || blockedSellerIds.has(item.seller_id)
}

export const filterBlockedItems = (items, blockedSellerIds) => {
    const normalized = Array.isArray(items) ? items : []
    if (!blockedSellerIds || blockedSellerIds.size === 0) return normalized
    return normalized.filter((item) => !isBlockedItem(item, blockedSellerIds))
}

/**
 * Adds a seller to the buyer's block list. `seller` is the account behind
 * the listing -- for a Shopify item that is the store's current owner, which
 * resolveSellerIdentity() gives, not the product's seller_id.
 */
export const blockSeller = async (buyerUid, seller) => {
    const sellerUid = seller?.id

    // Blocking yourself would hide your own listings from your own browse
    // grid with no way to tell why.
    if (!buyerUid || !sellerUid || sellerUid === buyerUid) return false

    try {
        const buyerRef = doc(db, 'users', buyerUid)
        const snapshot = await getDoc(buyerRef)
        const blocked = readBlockedSellers(snapshot.data())

        if (blocked.some((entry) => entry.id === sellerUid)) return true

        await updateDoc(buyerRef, {
            blockedSellers: [...blocked, {
                id: sellerUid,
                displayName: seller.displayName || '',
                picture: seller.picture || '',
                blocked_at: new Date()
            }]
        })

        clearBlockedSellerCache(buyerUid)
        return true
    } catch (error) {
        console.error('Error blocking seller:', error)
        return false
    }
}

export const unblockSeller = async (buyerUid, sellerUid) => {
    if (!buyerUid || !sellerUid) return false

    try {
        const buyerRef = doc(db, 'users', buyerUid)
        const snapshot = await getDoc(buyerRef)
        const blocked = readBlockedSellers(snapshot.data())

        await updateDoc(buyerRef, {
            blockedSellers: blocked.filter((entry) => entry.id !== sellerUid)
        })

        clearBlockedSellerCache(buyerUid)
        return true
    } catch (error) {
        console.error('Error unblocking seller:', error)
        return false
    }
}
